import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AgentDataApiService } from 'src/app/services/agent-data-api.service';
import { GenericContactSignals, AgentExtractedIdentitySignals } from 'src/app/shared/agent-data';
import { AgentQuickCreateResult } from '../agent-customer-quick-create/agent-customer-quick-create.component';

/**
 * AG-25 — standalone paste-text-to-create-client dialog. Accepts free-form
 * text (email signature, business card, etc.), parses it via LLM into
 * company/contact signals, and renders `AgentCustomerQuickCreateComponent`
 * inside the same dialog for the manager to review and save. Never persists
 * pasted text itself — parsed result lives only in local state until the
 * manager saves the resulting `Clients`/`Person` row.
 *
 * Two modes: 'new-company' for creating a new company from pasted text,
 * 'new-contact' for adding a contact to an existing company.
 *
 * On successful create, emits via `created` output and closes the dialog;
 * host decides what to do with the new CLIID/PERSID.
 */
@Component({
  selector: 'app-agent-paste-to-create',
  templateUrl: './agent-paste-to-create.component.html',
  styleUrls: ['./agent-paste-to-create.component.scss'],
})
export class AgentPasteToCreateComponent {
  @Input() mode: 'new-company' | 'new-contact' = 'new-company';
  /** Only read in 'new-contact' mode — the already-selected company's `CLIID`. */
  @Input() cliId: number | null = null;

  /** Emitted after a successful save in AgentCustomerQuickCreateComponent. */
  @Output() created = new EventEmitter<AgentQuickCreateResult>();

  visible = false;
  pastedText = '';
  loading = false;
  error: string | null = null;
  parsed: GenericContactSignals | null = null;

  constructor(private agentDataApi: AgentDataApiService) { }

  open(): void {
    this.visible = true;
    this.pastedText = '';
    this.error = null;
    this.parsed = null;
    this.loading = false;
  }

  onExtract(): void {
    if (!this.pastedText.trim()) {
      return;
    }

    this.loading = true;
    this.error = null;
    this.parsed = null;

    this.agentDataApi.extractIdentityFromText(this.pastedText).subscribe({
      next: (result) => {
        this.loading = false;
        this.parsed = result;
      },
      error: (err) => {
        this.loading = false;
        // Show error text verbatim from backend (it distinguishes "service not
        // configured" from "extraction failed")
        this.error = err.error?.error || err.message || 'Помилка розпізнавання';
      },
    });
  }

  onCreated(result: AgentQuickCreateResult): void {
    this.visible = false;
    this.created.emit(result);
  }

  /** Convert GenericContactSignals to AgentExtractedIdentitySignals by dropping the email field. */
  private toIdentitySignals(s: GenericContactSignals): AgentExtractedIdentitySignals {
    const { companyName, registrationCode, contactLastName, contactFullName, phone } = s;
    return { companyName, registrationCode, contactLastName, contactFullName, phone };
  }

  // Expose the conversion for the template
  getIdentitySignals(): AgentExtractedIdentitySignals | null {
    return this.parsed ? this.toIdentitySignals(this.parsed) : null;
  }

  getSenderEmail(): string | null {
    return this.parsed?.email ?? null;
  }

  /** Organization address, split into parts, if the pasted text stated one.
   *  Company data, not contact data -- see `AgentCustomerQuickCreateComponent`'s
   *  `addressPostalCode`/`addressCountry`/`addressCity`/`addressStreet` inputs. */
  getAddressPostalCode(): string | null {
    return this.parsed?.addressPostalCode ?? null;
  }

  getAddressCountry(): string | null {
    return this.parsed?.addressCountry ?? null;
  }

  getAddressCity(): string | null {
    return this.parsed?.addressCity ?? null;
  }

  getAddressStreet(): string | null {
    return this.parsed?.addressStreet ?? null;
  }
}
